import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { initializeMarketplaceContact } from '../../lib/messaging';
import { useAuthStore } from '../../store/authStore';
import Button from '../Button';
import { MessageSquare } from 'lucide-react';

interface MarketplaceContactProps {
  recipientId: string;
  listingId: string;
  jobDescription: string;
  onClose?: () => void;
}

export default function MarketplaceContact({
  recipientId,
  listingId,
  jobDescription,
  onClose
}: MarketplaceContactProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [message, setMessage] = useState(jobDescription);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/auth', { 
        state: { 
          error: 'Please sign in to contact providers',
          redirectTo: window.location.pathname
        }
      });
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // Initialize conversation and get ID
      const conversationId = await initializeMarketplaceContact(
        recipientId,
        listingId,
        message
      );
      
      // Navigate to messages with the conversation open
      navigate(`/messages?id=${conversationId}`);
      
      if (onClose) {
        onClose();
      }
    } catch (error: any) {
      console.error('Error contacting provider:', error);
      setError(error.message || 'Failed to send message');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-3 rounded-lg bg-secondary-dark bg-opacity-10 text-secondary-dark text-sm">
          {error}
        </div>
      )}

      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        className="w-full h-24 px-4 py-3 pl-4 rounded-full border border-primary-light bg-primary-light text-primary placeholder:text-neutral focus:border-surface-light focus:ring-0 focus:outline-none resize-none mt-4"
        placeholder="Edit your message..."
        disabled={isSubmitting}
      />
      <div className="flex justify-end items-center">
        <Button
          type="submit"
          variant="primary"
          className="w-44 text-sm text-surface-light"
          disabled={isSubmitting || !message.trim()}
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </Button>
      </div>
    </form>
  );
}